// src/utils/generateReceiptPDF.ts
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import QRCode from "qrcode";
import { getAthletesAggregate, getSlugsByAthleteIds } from "../firebase/athletes";
import { formatDateGeorgian } from "./dateHelpers";
import { categoryDisplayLabel } from "./rankingCalculator";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ReceiptAthlete {
  athleteId?: string;
  slug?: string;
  firstName: string;
  lastName: string;
  dateOfBirth?: string;
  category?: string;
  club: string;
  division: string;
  weightCategory: string;
  price: number;
}

export interface ReceiptData {
  referenceCode: string;
  competitionName: string;
  createdAt: any;
  totalAmount: number;
  /** "website" = online payment, "bank" = bank transfer / manual */
  paymentType: "website" | "bank";
  coachName?: string;
  coachEmail?: string;
  athletes: ReceiptAthlete[];
}

// ---------------------------------------------------------------------------
// Label dictionaries for bilingual PDF
// ---------------------------------------------------------------------------

const LABELS = {
  en: {
    orgName: "GEORGIAN TAEKWONDO FEDERATION",
    receiptTitle: "Registration Receipt",
    refCode: "Reference Code:",
    competition: "Competition:",
    date: "Date:",
    paymentStatus: "Payment Type:",
    coach: "Coach:",
    statusWebsite: "Paid on Website",
    statusBank: "Paid by Bank Transfer",
    tableHeaders: ["#", "Athlete", "DOB", "Category", "Club", "Division", "Weight", "Price", "QR"],
    total: "Total Amount:",
    authorisedBy: "Authorised by:",
    orgNameSig: "Georgian Taekwondo Federation",
    footer:
      "This is an electronic receipt — keep for your records. Official proof of competition registration.",
  },
  ka: {
    orgName: "საქართველოს ტაეკვონდოს ფედერაცია",
    receiptTitle: "რეგისტრაციის ქვითარი",
    refCode: "სარ. კოდი:",
    competition: "შეჯიბრება:",
    date: "თარიღი:",
    paymentStatus: "გადახდის ტიპი:",
    coach: "მწვრთნელი:",
    statusWebsite: "გადახდილია ვებ-გვერდზე",
    statusBank: "გადახდილია საბანკო გადარიცხვით",
    tableHeaders: ["#", "სპორტსმენი", "დ/ბ", "კატეგ.", "კლუბი", "განყ.", "წონა", "ფასი", "QR"],
    total: "სულ:",
    authorisedBy: "ავტორიზებულია:",
    orgNameSig: "საქართველოს ტაეკვონდოს ფედერაცია",
    footer:
      "ეს ელ. ქვითარი — შეინახეთ. შეჯიბრზე რეგისტრაციის ოფიციალური დასტური.",
  },
};

// ---------------------------------------------------------------------------
// Private helpers
// ---------------------------------------------------------------------------

const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

const fetchAsDataURL = async (path: string): Promise<string | null> => {
  try {
    const res = await fetch(path);
    if (!res.ok) return null;
    const blob = await res.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

const registerGeorgianFont = async (doc: jsPDF): Promise<string> => {
  const FONT_NAME = "NotoSansGeorgian";
  const FONT_PATH = "/fonts/NotoSansGeorgian-Regular.ttf";
  try {
    const res = await fetch(FONT_PATH);
    if (!res.ok) {
      console.warn("[PDF] Font not found – using helvetica");
      return "helvetica";
    }
    const buffer = await res.arrayBuffer();
    const b64 = arrayBufferToBase64(buffer);
    doc.addFileToVFS("NotoSansGeorgian-Regular.ttf", b64);
    doc.addFont("NotoSansGeorgian-Regular.ttf", FONT_NAME, "normal");
    return FONT_NAME;
  } catch (e) {
    console.error("[PDF] Font registration error:", e);
    return "helvetica";
  }
};

const makeQR = async (url: string): Promise<string | null> => {
  try {
    return await QRCode.toDataURL(url, {
      width: 128,
      margin: 1,
      color: { dark: "#000000", light: "#ffffff" },
      errorCorrectionLevel: "H",
    });
  } catch (e) {
    console.warn(`[PDF] QR failed for ${url}:`, e);
    return null;
  }
};

/**
 * Fetches dateOfBirth for athletes using their athleteId field (not doc ID).
 * Splits into chunks of 30 to respect Firestore's 'in' limit.
 */
const fetchMissingDOBs = async (
  athleteIds: string[]
): Promise<Record<string, string>> => {
  if (athleteIds.length === 0) return {};

  const requested = new Set(athleteIds);
  const results: Record<string, string> = {};
  const athletes = await getAthletesAggregate();
  athletes.forEach((athlete) => {
    if (requested.has(athlete.athleteId) && athlete.dateOfBirth) results[athlete.athleteId] = athlete.dateOfBirth;
  });
  return results;
};

// ---------------------------------------------------------------------------
// Page renderer (one language per call)
// ---------------------------------------------------------------------------

function renderPage(
  doc: jsPDF,
  data: ReceiptData,
  athletes: ReceiptAthlete[],
  qrDataUrls: (string | null)[],
  logoDataUrl: string | null,
  signatureDataUrl: string | null,
  activeFont: string,
  lang: "en" | "ka"
): void {
  const L = LABELS[lang];
  const PAGE_W = 210;
  const MARGIN = 14;
  const RED: [number, number, number] = [218, 41, 28];
  const QR_CELL_MM = 22;

  // Date formatting
  const formatDate = (): string => {
    if (!data.createdAt) return "—";
    const d = data.createdAt.toDate ? data.createdAt.toDate() : new Date(data.createdAt);
    if (lang === "ka") return formatDateGeorgian(d);
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };

  const paymentLabel =
    data.paymentType === "bank" ? L.statusBank : L.statusWebsite;

  // ── Header banner ─────────────────────────────────────────────────────
  doc.setFillColor(...RED);
  doc.rect(0, 0, PAGE_W, 34, "F");
  if (logoDataUrl) {
    doc.addImage(logoDataUrl, "PNG", MARGIN, 5, 22, 22);
  }
  doc.setTextColor(255, 255, 255);
  doc.setFont(activeFont, "normal");
  doc.setFontSize(13);
  doc.text(L.orgName, logoDataUrl ? 40 : MARGIN, 15);
  doc.setFontSize(9);
  doc.text(L.receiptTitle, logoDataUrl ? 40 : MARGIN, 25);

  // Language tag in top-right corner
  doc.setFontSize(8);
  doc.setTextColor(255, 255, 255);
  doc.text(lang === "en" ? "EN" : "KA", PAGE_W - MARGIN, 10, { align: "right" });

  // ── Meta block ────────────────────────────────────────────────────────
  doc.setTextColor(0, 0, 0);
  doc.setFont(activeFont, "normal");

  doc.setFontSize(13);
  doc.text(`${L.refCode} ${data.referenceCode}`, MARGIN, 47);

  doc.setFontSize(9.5);
  doc.text(`${L.competition} ${data.competitionName}`, MARGIN, 56);
  doc.text(`${L.date} ${formatDate()}`, MARGIN, 64);
  doc.text(`${L.paymentStatus} ${paymentLabel}`, MARGIN, 72);

  let metaY = 72;

  if (data.coachName || data.coachEmail) {
    metaY = 80;
    const coachStr = [data.coachName, data.coachEmail]
      .filter(Boolean)
      .join(" — ");
    doc.text(`${L.coach} ${coachStr}`, MARGIN, metaY);
  }

  // ── Athlete table ─────────────────────────────────────────────────────
  const tableStartY = metaY + 10;

  autoTable(doc, {
    startY: tableStartY,
    head: [L.tableHeaders],
    body: athletes.map((a, idx) => [
      idx + 1,
      `${a.firstName} ${a.lastName}`,
      a.dateOfBirth ?? "—",
      a.category ? categoryDisplayLabel(a.category) : "—",
      a.club,
      a.division,
      a.weightCategory,
      `${a.price} GEL`,
      "", // QR placeholder
    ]),
    headStyles: {
      fillColor: RED,
      textColor: 255,
      fontStyle: "normal",
      font: activeFont,
      fontSize: 8,
    },
    bodyStyles: {
      font: activeFont,
      fontSize: 8,
      minCellHeight: QR_CELL_MM,
    },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    styles: { font: activeFont, fontSize: 8, overflow: "linebreak" },
    columnStyles: {
      0: { cellWidth: 7 },
      1: { cellWidth: 34 },
      2: { cellWidth: 20 },
      3: { cellWidth: 18 },
      8: { cellWidth: QR_CELL_MM },
    },
    didDrawCell: (cellData) => {
      if (cellData.section === "body" && cellData.column.index === 8) {
        const qr = qrDataUrls[cellData.row.index];
        if (qr) {
          const padding = 1;
          doc.addImage(
            qr,
            "PNG",
            cellData.cell.x + padding,
            cellData.cell.y + padding,
            QR_CELL_MM - padding * 2,
            QR_CELL_MM - padding * 2
          );
        }
      }
    },
  });

  const finalY = (doc as any).lastAutoTable.finalY;
  const totalY = finalY + 10;

  // ── Total line ────────────────────────────────────────────────────────
  doc.setDrawColor(...RED);
  doc.line(MARGIN, totalY - 4, PAGE_W - MARGIN, totalY - 4);
  doc.setFont(activeFont, "normal");
  doc.setFontSize(12);
  doc.setTextColor(0, 0, 0);
  doc.text(`${L.total} ${data.totalAmount} GEL`, MARGIN, totalY + 4);

  // ── Signature block ───────────────────────────────────────────────────
  const sigY = totalY + 20;
  if (signatureDataUrl) {
    doc.addImage(signatureDataUrl, "PNG", PAGE_W - MARGIN - 50, sigY, 50, 20);
  }
  doc.setFont(activeFont, "normal");
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text(L.authorisedBy, PAGE_W - MARGIN - 50, sigY + 26);
  doc.text(L.orgNameSig, PAGE_W - MARGIN - 50, sigY + 32);

  // ── Footer ────────────────────────────────────────────────────────────
  doc.setFont(activeFont, "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(160, 160, 160);
  doc.text(L.footer, MARGIN, 286);
}

// ---------------------------------------------------------------------------
// Core renderer — builds both EN and KA pages in a single PDF
// ---------------------------------------------------------------------------

async function renderReceiptPDF(data: ReceiptData): Promise<void> {
  console.log(`[PDF] Generating receipt ${data.referenceCode}`);

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const activeFont = await registerGeorgianFont(doc);

  const [logoDataUrl, signatureDataUrl] = await Promise.all([
    fetchAsDataURL("/geo.png"),
    fetchAsDataURL("/signature.png"),
  ]);

  // Resolve missing slugs
  const missingSlugIds = data.athletes
    .filter((a) => a.athleteId && !a.slug)
    .map((a) => a.athleteId as string);
  let fetchedSlugs: Record<string, string> = {};
  if (missingSlugIds.length > 0) {
    fetchedSlugs = await getSlugsByAthleteIds(missingSlugIds);
  }

  // Resolve missing DOBs from Firestore athletes collection
  const missingDOBIds = data.athletes
    .filter((a) => !a.dateOfBirth && a.athleteId)
    .map((a) => a.athleteId as string);
  let fetchedDOBs: Record<string, string> = {};
  if (missingDOBIds.length > 0) {
    fetchedDOBs = await fetchMissingDOBs(missingDOBIds);
  }

  // Build enriched athletes array
  const enrichedAthletes: ReceiptAthlete[] = data.athletes.map((a) => ({
    ...a,
    slug: a.slug || (a.athleteId ? fetchedSlugs[a.athleteId] : undefined),
    dateOfBirth:
      a.dateOfBirth || (a.athleteId ? fetchedDOBs[a.athleteId] : undefined),
  }));

  // Generate QR codes once — reused on both pages
  const qrDataUrls = await Promise.all(
    enrichedAthletes.map((a) =>
      a.slug
        ? makeQR(`https://taekwondo.ge/struktura/members/${a.slug}`)
        : Promise.resolve(null)
    )
  );

  // Page 1 — English
  renderPage(
    doc,
    data,
    enrichedAthletes,
    qrDataUrls,
    logoDataUrl,
    signatureDataUrl,
    activeFont,
    "en"
  );

  // Page 2 — Georgian
  doc.addPage();
  renderPage(
    doc,
    data,
    enrichedAthletes,
    qrDataUrls,
    logoDataUrl,
    signatureDataUrl,
    activeFont,
    "ka"
  );

  doc.save(`receipt-${data.referenceCode}.pdf`);
  console.log(`[PDF] Saved receipt-${data.referenceCode}.pdf`);
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Generate a receipt PDF for a PAID registration (from the payments collection).
 * Automatically fetches missing DOBs from the athletes collection.
 */
export async function generatePaymentPDF(
  payment: any,
  opts?: { coachName?: string; coachEmail?: string }
): Promise<void> {
  await renderReceiptPDF({
    referenceCode: payment.referenceCode,
    competitionName: payment.competitionName,
    createdAt: payment.createdAt,
    totalAmount: payment.totalAmount ?? 0,
    paymentType: payment.isFree ? "bank" : "website",
    coachName: opts?.coachName ?? payment.coachName,
    coachEmail: opts?.coachEmail ?? payment.coachEmail,
    athletes: payment.athletes ?? [],
  });
}

/**
 * Generate a receipt PDF when you already have all the data (no Firestore joins needed).
 * Used by RegistrationSuccess, CoachHistory, and AdminRecentRegistrations for manual entries.
 */
export async function generateDirectReceiptPDF(data: ReceiptData): Promise<void> {
  await renderReceiptPDF(data);
}

/**
 * @deprecated Use generateDirectReceiptPDF instead.
 * Kept for backward compatibility with any existing callers.
 */
export async function generateManualReceiptPDF(
  referenceCode: string,
  competitionName: string,
  registeredAt: any,
  athletes: ReceiptAthlete[],
  opts?: { coachName?: string; coachEmail?: string; totalAmount?: number }
): Promise<void> {
  const total = opts?.totalAmount ?? athletes.reduce((s, a) => s + a.price, 0);
  await renderReceiptPDF({
    referenceCode,
    competitionName,
    createdAt: registeredAt,
    totalAmount: total,
    paymentType: "bank",
    coachName: opts?.coachName,
    coachEmail: opts?.coachEmail,
    athletes,
  });
}
